import { useGlobalContext } from "../../../../../Context/Context";
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import Button from "../../../../Custom/Button/Button";
import TabelaSredstvaOprema from "./TabelaSredstvaOprema";
import PreviewDokumenta from "./PreviewDokumenta";
import ListaInventara from "../lista-inventara/ListaInventara";
import ZahtevZaPregledProveru from "./zahtev-za-pregled-proveru/ZahtevZaPregledProveru";

const styles = {
  button: { width: "33%", height: "40px", cursor: "pointer" },
};

function MasineUredjaji() {
  const [currentState, setCurrentState] = useState("AKTIVNA");
  const [sredstvoId, setSredstvoId] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [editSredstvo, setEditSredstvo] = useState(null);
  const [showListaInventara, setShowListaInventara] = useState(null);

  const {
    dictionary,
    baseUrl,
    apiKey,
    loggedUser,
    sredstvaOpremaPretraga,
    setSredstvaOpremaPretraga,
    setSelectedSredstvaOprema,
    setEditSredstvoData,
    masineUredjaji,
    setMasineUredjaji,
    selectedMainEntity,
    isZahtevZaPregledSredstva,
  } = useGlobalContext();

  // Mapiranje: levi panel → backend state
  const mapViewToStatus = (mode) => {
    if (mode === "AKTIVNA") return "AKTIVNA";
    if (mode === "ARHIVIRANE") return "NEAKTIVNA";
    if (mode === "TUDJE") return "TUDJA";
    return "AKTIVNA";
  };

  // Mapiranje: backend state → levi panel
  const mapStatusToView = (status) => {
    if (status === "AKTIVNA") return "AKTIVNA";
    if (status === "NEAKTIVNA") return "ARHIVIRANE";
    if (status === "TUDJA") return "TUDJE";
    return "AKTIVNA";
  };

  // Kad god se promeni currentState, sinhronizuj “utisnuti” taster levo
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("machinesViewSync", {
        detail: { mode: mapStatusToView(currentState) },
      }),
    );
  }, [currentState]);

  const handleNazad = () => {
    if (editSredstvo) {
      setEditSredstvo(null);
      setEditSredstvoData(null);
      setExpandedRowId(null);
    } else {
      setSredstvoId("");
      setSelectedSredstvaOprema(0);
      setSredstvaOpremaPretraga(null);
      setSelectedRows([]);
      setCurrentState("AKTIVNA");
    }
  };

  const fetchMasine = useCallback(async () => {
    if (!loggedUser?.id || !apiKey) return;
    setLoading(true);
    try {
      const payload = {
        user_id: loggedUser.id,
        api_key: apiKey,
        company_id: selectedMainEntity.company_id,
        status_filter: currentState,
      };
      const { data } = await axios.post(
        `${baseUrl}/machines/read_machines.php`,
        payload,
      );
      if (data.status === 200) {
        setMasineUredjaji(data.data || []);
      } else {
        setMasineUredjaji([]);
      }
    } catch (error) {
      setMasineUredjaji([]);
    } finally {
      setLoading(false);
    }
  }, [currentState, loggedUser, apiKey, baseUrl]);

  useEffect(() => {
    const onModeChange = (e) => {
      const mode = e?.detail?.mode;
      if (!mode) return;

      setSelectedRows([]);
      setSredstvoId("");

      const nextStatus = mapViewToStatus(mode);
      setCurrentState(nextStatus);
    };

    window.addEventListener("machinesViewChange", onModeChange);
    return () => window.removeEventListener("machinesViewChange", onModeChange);
  }, []);

  useEffect(() => {
    const handleRefresh = () => fetchMasine();
    window.addEventListener("machineAdded", handleRefresh);
    return () => window.removeEventListener("machineAdded", handleRefresh);
  }, [fetchMasine]);

  useEffect(() => {
    fetchMasine();
  }, [fetchMasine]);

  const filtrovaneMasine = useMemo(() => {
    if (editSredstvo)
      return masineUredjaji.filter((m) => m.masina_id === editSredstvo.id);

    if (!sredstvaOpremaPretraga) return masineUredjaji;

    let filtered = [...masineUredjaji];

    if (
      sredstvaOpremaPretraga.vrsta?.id &&
      sredstvaOpremaPretraga.vrsta.id !== "sve" &&
      sredstvaOpremaPretraga.vrsta.id !== -1
    ) {
      filtered = filtered.filter(
        (m) => m.tip === sredstvaOpremaPretraga.vrsta.id,
      );
    }

    if (
      sredstvaOpremaPretraga.poslovnaJedinica?.id &&
      sredstvaOpremaPretraga.poslovnaJedinica.id !== "sve" &&
      sredstvaOpremaPretraga.poslovnaJedinica.id !== -1
    ) {
      filtered = filtered.filter(
        (m) =>
          m.poslovna_jedinica_id == sredstvaOpremaPretraga.poslovnaJedinica.id,
      );
    }

    if (
      sredstvaOpremaPretraga.istekDokumenta?.id &&
      sredstvaOpremaPretraga.istekDokumenta.id !== "sve" &&
      sredstvaOpremaPretraga.istekDokumenta.id !== -1
    ) {
      const istekId = sredstvaOpremaPretraga.istekDokumenta.id;
      filtered = filtered.filter((m) => {
        const statusi = [m.td, m.sn, m.rg, m.pu, m.od].filter(Boolean);
        if (istekId === "1") return statusi.includes("wrong");
        if (istekId === "2") return statusi.includes("notif");
        if (istekId === "3")
          return !statusi.includes("wrong") && !statusi.includes("notif");
        return true;
      });
    }

    if (sredstvaOpremaPretraga.pretraga) {
      const searchTerm = sredstvaOpremaPretraga.pretraga.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          (m.naziv && m.naziv.toLowerCase().includes(searchTerm)) ||
          (m.identifikacioni_broj &&
            m.identifikacioni_broj.toLowerCase().includes(searchTerm)),
      );
    }

    return filtered;
  }, [masineUredjaji, sredstvaOpremaPretraga, editSredstvo]);

  const handleArchiveSelected = async () => {
    try {
      await axios.post(`${baseUrl}/machines/status_bulk.php`, {
        user_id: loggedUser.id,
        api_key: apiKey,
        company_id: 1,
        status: currentState === "AKTIVNA" ? "NEAKTIVNA" : "AKTIVNA",
        ids: selectedRows.map((id) => Number(id)),
      });
      setMasineUredjaji((prev) =>
        prev.filter((m) => !selectedRows.includes(m.masina_id)),
      );
      setSelectedRows([]);
    } catch (err) {
      console.log("ERROR", err);
    }
  };

  const handleOpenInventar = () => {
    setShowListaInventara(true);
  };
  const handleCloseInventar = () => {
    setShowListaInventara(false);
  };

  if (isZahtevZaPregledSredstva)
    return <ZahtevZaPregledProveru setCurrentState={setCurrentState} />;

  if (showListaInventara && editSredstvo)
    return (
      <ListaInventara
        sredstvoId={editSredstvo.id}
        close={handleCloseInventar}
      />
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          padding: "25px",
          backgroundColor: "#f9f9f9",
          height: "85%",
          overflow: "auto",
          border: "1.5px solid #9f9f9f",
        }}
      >
        {!sredstvoId && (
          <>
            {loading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Učitavanje...
              </div>
            ) : (
              <TabelaSredstvaOprema
                data={filtrovaneMasine}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                editSredstvo={editSredstvo}
                setEditSredstvo={setEditSredstvo}
                expandedRowId={expandedRowId}
                setExpandedRowId={setExpandedRowId}
                fetchMasine={fetchMasine}
              />
            )}
          </>
        )}

        {sredstvoId && (
          <PreviewDokumenta
            sredstvoId={sredstvoId}
            setSredstvoId={setSredstvoId}
          />
        )}
      </div>

      <div
        className="tasteri-tabele"
        style={{
          display: "flex",
          justifyContent: "space-between",
          height: "50px",
        }}
      >
        {!editSredstvo && (
          <div
            className="taster-tabele-left"
            style={{
              display: "flex",
            }}
          >
            <Button
              style={{
                width: "230px",
                fontWeight: "bold",
                marginRight: "20px",
              }}
            >
              ШТАМПАЈ
            </Button>
            <Button style={{ width: "230px", fontWeight: "bold" }}>
              ПОШАЉИ
            </Button>
          </div>
        )}
        {editSredstvo && (
          <div style={{ display: "flex", gap: "10px" }}>
            <div onClick={handleOpenInventar}>
              <Button
                activation={true}
                style={{
                  width: "230px",
                  fontWeight: "bold",
                }}
              >
                ИД КАРТА
              </Button>
            </div>
          </div>
        )}

        <div
          className="taster-tabele-right"
          style={{
            display: "flex",
          }}
        >
          {selectedRows.length > 0 && (
            <div onClick={handleArchiveSelected}>
              <Button
                activation={true}
                style={{
                  width: "230px",
                  fontWeight: "bold",
                  marginRight: "20px",
                }}
              >
                {currentState === "AKTIVNA" ? "АРХИВИРАЈ" : "AKTIVIRAJ"}
              </Button>
            </div>
          )}

          <div onClick={handleNazad} style={{ height: "100%" }}>
            <Button
              activation={true}
              style={{ width: "230px", fontWeight: "bold" }}
            >
              НАЗАД
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MasineUredjaji;
